import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, RefreshCw, Trash2, CheckCircle2, AlertCircle, ExternalLink, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const HINTS = {
  gmail: "Su Gmail con 2FA attiva serve una password app-specifica: myaccount.google.com/apppasswords",
  icloud: "Su iCloud serve una password app-specifica: appleid.apple.com → Sicurezza → Password specifiche per app",
  yahoo: "Su Yahoo Mail serve una password app-specifica dalle impostazioni account",
  outlook: "Outlook/Hotmail: potrebbe servire password app-specifica se hai 2FA",
};

export default function EmailPage() {
  const [status, setStatus] = useState(null);
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [form, setForm] = useState({
    provider: "gmail",
    email_address: "",
    password: "",
    host: "",
    port: 993,
  });
  const [lastResult, setLastResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([api.get("/email/status"), api.get("/email/presets")]);
      setStatus(s.data);
      setPresets(p.data.presets);
    } catch (err) { console.warn("email status", err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const p = presets.find(x => x.key === form.provider);
    if (p) setForm(f => ({ ...f, host: p.host, port: p.port }));
  }, [form.provider, presets]);

  const connect = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/email/connect", {
        provider: form.provider,
        email_address: form.email_address,
        password: form.password,
        host: form.provider === "custom" ? form.host : undefined,
        port: form.provider === "custom" ? Number(form.port) : undefined,
      });
      toast.success("Account email collegato");
      setForm(f => ({ ...f, password: "" }));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore di connessione");
    } finally { setLoading(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Scollegare l'account email? Le credenziali salvate saranno eliminate.")) return;
    try {
      await api.delete("/email/disconnect");
      toast.success("Account scollegato");
      setLastResult(null);
      load();
    } catch { toast.error("Errore"); }
  };

  const sync = async () => {
    setSyncing(true);
    setLastResult(null);
    try {
      const r = await api.post("/email/sync", { max_messages: 50, since_days: 30 });
      setLastResult(r.data);
      toast.success(`${r.data.imported} allegati importati`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore di sincronizzazione");
    } finally { setSyncing(false); }
  };

  const selectedPreset = presets.find(p => p.key === form.provider);
  const hint = HINTS[form.provider] || selectedPreset?.hint;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-8">
      <div className="mb-8">
        <div className="overline text-[#0A0A0A] mb-3">— IMPORT AUTOMATICO</div>
        <h1 className="font-display font-bold text-4xl tracking-tighter mb-2">Collega la tua email</h1>
        <p className="text-sm text-[#5C5C5C] max-w-xl">
          Funziona con <b>qualsiasi provider IMAP</b>: Gmail, Outlook, iCloud, Libero, Aruba, Yahoo, aziendali.
          L'app scarica solo scontrini, ricevute, fatture (immagini e PDF).
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-[#8A8A8A]">Caricamento…</div>
      ) : status?.connected ? (
        <div className="space-y-6">
          <div className="bg-white border border-[#009966]/30 rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#009966]/10 flex items-center justify-center rounded-xl">
                  <CheckCircle2 className="w-5 h-5 text-[#009966]" />
                </div>
                <div>
                  <div className="overline text-[#009966] mb-1">CONNESSA</div>
                  <div className="font-display font-bold text-lg tracking-tight" data-testid="email-connected-address">{status.email_address}</div>
                  <div className="text-xs text-[#5C5C5C] font-mono-plex mt-0.5">{status.host}</div>
                </div>
              </div>
              <button
                data-testid="email-disconnect-btn"
                onClick={disconnect}
                className="text-[#FF3333] hover:bg-[#FF3333]/5 p-2 rounded-xl transition-colors"
                title="Scollega"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4 text-xs text-[#5C5C5C] font-mono-plex border-t border-black/5 pt-4">
              <div>
                <div className="overline text-[#8A8A8A] mb-1">ULTIMA SYNC</div>
                <div>{status.last_sync ? new Date(status.last_sync).toLocaleString("it-IT") : "Mai"}</div>
              </div>
              <div>
                <div className="overline text-[#8A8A8A] mb-1">ULTIMI IMPORTATI</div>
                <div>{status.last_imported || 0} allegati</div>
              </div>
            </div>
          </div>

          <div className="bg-white border border-black/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0A0A0A]/5 flex items-center justify-center rounded-xl">
                  <Zap className="w-5 h-5 text-[#0A0A0A]" />
                </div>
                <div>
                  <div className="overline text-[#8A8A8A] mb-1">SINCRONIZZAZIONE AUTOMATICA</div>
                  <div className="font-display font-bold text-lg tracking-tight">Ogni notte</div>
                  <div className="text-xs text-[#5C5C5C]">L'app scarica automaticamente i nuovi allegati mentre dormi.</div>
                </div>
              </div>
              <Switch
                data-testid="email-auto-sync-toggle"
                checked={!!status.auto_sync}
                onCheckedChange={async (v) => {
                  try {
                    await api.post("/email/auto-sync", { enabled: v });
                    toast.success(v ? "Sync automatico attivo" : "Sync automatico disattivato");
                    load();
                  } catch { toast.error("Errore"); }
                }}
              />
            </div>
          </div>

          <div className="bg-white border border-black/10 rounded-xl p-6">
            <div className="overline text-[#8A8A8A] mb-4">SINCRONIZZAZIONE MANUALE</div>
            <p className="text-sm text-[#5C5C5C] mb-4">
              Scarica gli allegati (immagini, PDF) delle ultime email e li elabora con l'AI.
              Ogni documento viene rinominato e archiviato nella cartella corretta — inclusa la <b>sezione fiscale</b> se rilevante.
            </p>
            <Button
              data-testid="email-sync-btn"
              onClick={sync}
              disabled={syncing}
              className="rounded-xl bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white h-11"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Sincronizzazione…" : "Sincronizza ora (ultimi 30 giorni)"}
            </Button>

            {lastResult && (
              <div className="mt-6 border-t border-black/5 pt-4">
                <div className="text-sm mb-2">
                  <b className="text-[#009966]">{lastResult.imported}</b> importati
                  {lastResult.skipped > 0 && <span className="text-[#8A8A8A]"> · {lastResult.skipped} saltati</span>}
                </div>
                {lastResult.documents?.length > 0 && (
                  <div className="space-y-1.5 mt-3">
                    {lastResult.documents.slice(0, 5).map(d => (
                      <div key={d.id} className="text-xs flex justify-between border-b border-black/5 py-1.5">
                        <span className="truncate">{d.smart_filename}</span>
                        <span className="text-[#8A8A8A] ml-2">{d.folder_name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <form onSubmit={connect} className="bg-white border border-black/10 rounded-xl p-6 space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-widest">Provider</Label>
            <Select value={form.provider} onValueChange={v => setForm({ ...form, provider: v })}>
              <SelectTrigger data-testid="email-provider-select" className="rounded-xl mt-2 h-11 bg-white border-black/15"><SelectValue /></SelectTrigger>
              <SelectContent>
                {presets.map(p => (
                  <SelectItem key={p.key} value={p.key}>
                    {p.key === "gmail" ? "Gmail" :
                     p.key === "outlook" ? "Outlook / Hotmail / Live" :
                     p.key === "icloud" ? "iCloud" :
                     p.key === "libero" ? "Libero" :
                     p.key === "aruba" ? "Aruba" :
                     p.key === "yahoo" ? "Yahoo" :
                     p.key === "tiscali" ? "Tiscali" :
                     "Personalizzato (altro provider IMAP)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hint && (
              <div className="mt-2 text-xs text-[#5C5C5C] flex items-start gap-1.5">
                <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#FFB800]" />
                <span>{hint} {form.provider === "gmail" && (
                  <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-[#0A0A0A] underline inline-flex items-center gap-0.5">
                    apri <ExternalLink className="w-3 h-3" />
                  </a>
                )}</span>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="email-addr" className="text-xs uppercase tracking-widest">Indirizzo email</Label>
            <Input
              data-testid="email-address-input"
              id="email-addr"
              type="email"
              required
              value={form.email_address}
              onChange={e => setForm({ ...form, email_address: e.target.value })}
              className="rounded-xl mt-2 bg-white h-11 focus-visible:ring-[#0A0A0A]"
              placeholder="tuo@indirizzo.com"
            />
          </div>

          <div>
            <Label htmlFor="email-pass" className="text-xs uppercase tracking-widest">Password (o password app-specifica)</Label>
            <Input
              data-testid="email-password-input"
              id="email-pass"
              type="password"
              required
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              className="rounded-xl mt-2 bg-white h-11 focus-visible:ring-[#0A0A0A]"
            />
          </div>

          {form.provider === "custom" && (
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-widest">Host IMAP</Label>
                <Input
                  data-testid="email-host-input"
                  required
                  value={form.host}
                  onChange={e => setForm({ ...form, host: e.target.value })}
                  className="rounded-xl mt-2 bg-white h-11"
                  placeholder="imap.esempio.com"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Porta</Label>
                <Input
                  data-testid="email-port-input"
                  type="number"
                  value={form.port}
                  onChange={e => setForm({ ...form, port: e.target.value })}
                  className="rounded-xl mt-2 bg-white h-11"
                />
              </div>
            </div>
          )}

          <Button
            data-testid="email-connect-btn"
            type="submit"
            disabled={loading}
            className="w-full rounded-xl bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white h-11"
          >
            <Mail className="w-4 h-4 mr-2" /> {loading ? "Connessione…" : "Collega account"}
          </Button>

          <p className="text-xs text-[#8A8A8A] leading-relaxed">
            🔒 Le credenziali sono cifrate lato server (AES-256/Fernet).
            Consigliato usare una <b>password app-specifica</b> se il tuo provider la supporta.
          </p>
        </form>
      )}
    </div>
  );
}
